import React from "react";
import styled from "styled-components";
import { formatCurrency } from "../config/market";

/**
 * DealTag: the "ticket stub" badge a discounted product wears.
 *
 * Sits flush in the top-left corner of the product image container (or the
 * top-right one, mirrored, when the top-left already holds an "Open" pill).
 * The outer corner it sits in is rounded to the same radius as the image
 * container so the two edges read as one shape; the inner top corner is
 * square; the bottom edge is a sawtooth, the way a coupon stub tears off.
 *
 * The shape is one inline SVG path drawn at the badge's exact pixel size, so
 * it stays crisp at 1x and 2x. The two text lines are HTML laid over the body
 * of the stub (above the teeth) so they use the site's own font stack.
 */

export const DEAL_GREEN = "#2D8A4E";
const FONT_STACK = '"Urbanist", "Inter", "Segoe UI", sans-serif';

/* width x height is the whole stub, teeth included. The body is the flat part
   above the teeth: height minus tooth depth. */
const SIZES = {
  s: {
    width: 40,
    height: 46,
    teeth: 5,
    toothWidth: 8,
    toothDepth: 4,
    pct: { size: 12, line: 13 },
    off: { size: 8, line: 10 },
  },
  m: {
    width: 48,
    height: 56,
    teeth: 6,
    toothWidth: 8,
    toothDepth: 4,
    pct: { size: 15, line: 16 },
    off: { size: 10, line: 12 },
  },
  l: {
    width: 60,
    height: 70,
    teeth: 6,
    toothWidth: 10,
    toothDepth: 5,
    pct: { size: 20, line: 22 },
    off: { size: 11, line: 13 },
  },
};

/**
 * One closed path: an arc on the outer top corner, straight top, straight
 * outer side, a sawtooth bottom drawn right to left, and a straight inner
 * side back up. `corner` picks which top corner carries the arc.
 */
export const stubPath = (spec, radius, corner) => {
  const { width: w, height: h, teeth, toothWidth: tw, toothDepth: td } = spec;
  const body = h - td;
  const r = Math.max(0, Math.min(Number(radius) || 0, w / 2, body / 2));
  const parts = [];

  if (corner === "right") {
    parts.push("M0 0", `H${w - r}`, `A${r} ${r} 0 0 1 ${w} ${r}`, `V${body}`);
  } else {
    parts.push(`M${r} 0`, `H${w}`, `V${body}`);
  }

  for (let i = 0; i < teeth; i += 1) {
    const x = w - i * tw;
    parts.push(`L${x - tw / 2} ${h}`, `L${x - tw} ${body}`);
  }

  if (corner === "right") {
    parts.push("Z");
  } else {
    parts.push(`V${r}`, `A${r} ${r} 0 0 1 ${r} 0`, "Z");
  }
  return parts.join(" ");
};

const Stub = styled.span`
  position: absolute;
  top: 0;
  ${(p) => (p.$corner === "right" ? "right: 0;" : "left: 0;")}
  width: ${(p) => p.$width}px;
  height: ${(p) => p.$height}px;
  display: block;
  pointer-events: none;
  z-index: ${(p) => p.$zIndex};
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.18));
  font-family: ${FONT_STACK};
  color: #ffffff;
  line-height: 1;

  svg {
    display: block;
    width: ${(p) => p.$width}px;
    height: ${(p) => p.$height}px;
  }
`;

const StubText = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  width: ${(p) => p.$width}px;
  height: ${(p) => p.$body}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const Percent = styled.span`
  display: block;
  font-size: ${(p) => p.$size}px;
  line-height: ${(p) => p.$line}px;
  font-weight: 800;
  letter-spacing: -0.01em;
  white-space: nowrap;
`;

const Off = styled.span`
  display: block;
  font-size: ${(p) => p.$size}px;
  line-height: ${(p) => p.$line}px;
  font-weight: 800;
  letter-spacing: 1px;
  /* letter-spacing trails the last glyph; nudge so the word sits centred */
  padding-left: 1px;
  text-transform: uppercase;
  white-space: nowrap;
`;

const DealTag = ({
  discount = null,
  percentOff,
  size = "m",
  radius = 12,
  corner = "left",
  zIndex = 1,
  className,
  style,
}) => {
  if (discount == null && percentOff == null) return null;
  const pct = Math.round(Number(percentOff ?? discount?.percentOff));
  if (!(pct > 0)) return null;

  const spec = SIZES[size] || SIZES.m;
  const side = corner === "right" ? "right" : "left";
  const body = spec.height - spec.toothDepth;

  return (
    <Stub
      className={className}
      style={style}
      $corner={side}
      $width={spec.width}
      $height={spec.height}
      $zIndex={zIndex}
      aria-label={`${pct}% off`}
      role="img"
    >
      <svg
        viewBox={`0 0 ${spec.width} ${spec.height}`}
        width={spec.width}
        height={spec.height}
        shapeRendering="geometricPrecision"
        aria-hidden="true"
        focusable="false"
      >
        <path d={stubPath(spec, radius, side)} fill={DEAL_GREEN} />
      </svg>
      <StubText $width={spec.width} $body={body} aria-hidden="true">
        <Percent $size={spec.pct.size} $line={spec.pct.line}>
          {pct}%
        </Percent>
        <Off $size={spec.off.size} $line={spec.off.line}>
          OFF
        </Off>
      </StubText>
    </Stub>
  );
};

/**
 * DealPill: the small solid green pill an AMOUNT store offer wears on a
 * vendor card ("Save UGX 2,000"), where a percentage stub would have nothing
 * honest to say.
 */
export const DealPill = styled.span`
  position: absolute;
  top: 10px;
  ${(p) => (p.$corner === "right" ? "right: 10px;" : "left: 10px;")}
  display: inline-flex;
  align-items: center;
  padding: 4px 9px;
  border-radius: 999px;
  background: ${DEAL_GREEN};
  color: #ffffff;
  font-family: ${FONT_STACK};
  font-size: 11px;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: 0.02em;
  white-space: nowrap;
  pointer-events: none;
  z-index: ${(p) => p.$zIndex ?? 1};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.18);
`;

/**
 * The vendor-card version: reads a vendor's `offerSummary` (null unless the
 * store offer is live right now) and wears the stub for a PERCENT offer or
 * the pill for an AMOUNT one.
 */
export const VendorOfferTag = ({
  offer,
  corner = "left",
  radius = 12,
  size = "m",
  zIndex = 1,
}) => {
  if (!offer) return null;
  const side = corner === "right" ? "right" : "left";
  if (offer.type === "PERCENT") {
    return (
      <DealTag
        percentOff={offer.percentOff}
        size={size}
        radius={radius}
        corner={side}
        zIndex={zIndex}
      />
    );
  }
  const amount = Number(offer.amountOff ?? offer.value);
  if (offer.type === "AMOUNT" && amount > 0) {
    return (
      <DealPill $corner={side} $zIndex={zIndex}>
        Save {formatCurrency(amount)}
      </DealPill>
    );
  }
  return null;
};

export default DealTag;
